import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId, stickersContainerId, Tag} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {boardViewStore} from '@components/board-view'
import {ButtonNew, IconButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {TippyInstance} from '@components/common/dropdown/dropdown'
import {useProjectStore} from '@components/project/project-store'
import {Tab} from '@components/sticker/my-stickers/my-stickers-store'
import {StickerItem} from '@components/sticker/sticker-item'
import {
  TwCollapseButton,
  TwHiddenStickersCount,
  TwLeftArea,
  TwRightArea,
  TwStickerPanel,
  TwStickerPanelWrapper,
  TwStickers
} from '@components/sticker/sticker-panel/containers'
import {WithResizeObserver} from '@components/sticker/sticker-panel/resize-observer'
import {stickerPanelGlobalStore} from '@components/sticker/sticker-panel/sticker-panel-global-store'
import {useSyncEvent} from '@containers/hooks'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Sticker as StickerExtension} from '@extensions/sticker/sticker'
import {StickerFilter} from '@extensions/sticker/sticker-filter'
import {
  StickerPanelContext,
  StickerPanelLocation,
  StickerPanelStore,
  useStickerPanelStore
} from './sticker-panel-store'

export interface StickerPanelProps {
  location?: StickerPanelLocation
  emulated?: {
    companyId: DataObjectId
    stickers: Tag[]
    filter?: StickerFilter
  }
}

export const StickerPanel = observer((props: StickerPanelProps) => {
  const boardView = boardViewStore.state.view
  const [store] = useState(() => new StickerPanelStore(boardView, props))

  useEffect(() => {
    store.setView(boardView)
    store.setLocation(props.location)
    store.syncIsEditAvailable()
    store.syncPanelStickers()
    store.syncFiltersCount()
  }, [boardView, props.location])

  return (
    <StickerPanelContext.Provider value={store}>
      <StickerPanelInner />
    </StickerPanelContext.Provider>
  )
})

// eslint-disable-next-line max-lines-per-function
const StickerPanelInner = observer(() => {
  const [projectState] = useProjectStore()
  const [state, store] = useStickerPanelStore()

  const panelWrapper = useRef<HTMLDivElement>()
  const leftArea = useRef<HTMLDivElement>(null)
  const stickersList = useRef<HTMLDivElement>()
  const rightArea = useRef<HTMLDivElement>()
  const stickersMenu = useRef<TippyInstance[]>([])

  const reCountSizes = useCallback(() => {
    if (
      panelWrapper.current &&
      leftArea.current &&
      rightArea.current &&
      stickersList.current?.children
    ) {
      store.recountPanelSizes(
        {
          panelWrapper: panelWrapper.current,
          leftArea: leftArea.current,
          rightArea: rightArea.current
        },
        stickersList.current.children
      )

      // hack to move tippy menu to the item if sticker changed the position
      stickersMenu.current.forEach(stickerMenu => {
        if (stickerMenu.popperInstance) {
          setTimeout(() => {
            stickerMenu?.popperInstance?.update()
          }, 0)
        }
      })
    }
  }, [])

  useEffect(() => {
    store.syncFiltersInHiddenArea()
  }, [state.stickersAreaWidth])

  useEffect(() => {
    window.addEventListener('resize', reCountSizes)
    return () => {
      window.removeEventListener('resize', reCountSizes)
    }
  }, [reCountSizes])

  useEffect(() => {
    reCountSizes()
  }, [state.view])

  useEffect(() => {
    if (projectState?.boardId) {
      stickerPanelGlobalStore.syncBoardId()
      store.syncFiltersController()
      store.syncStickers()
      store.syncIsEditAvailable()
      reCountSizes()
    }
  }, [projectState?.boardId, reCountSizes])

  useEffect(() => {
    reCountSizes()
  }, [store.stickersList.length])

  useSyncEvent(
    'current',
    {
      debounceTime: 50,
      filter: () => [stickersContainerId]
    },
    () => {
      store.syncStickers()
    }
  )

  useSyncEvent(
    'current',
    {
      debounceTime: 1000,
      filter: () => [PageService.getCurrentBoard()?.id]
    },
    () => {
      store.syncStickers()
    }
  )

  useEffect(() => {
    const listener = BoardFilters.addListener(() => {
      store.syncFiltersCount()
      // for cases when reset button is alone on a line
      setTimeout(() => {
        reCountSizes()
      }, 0)
    })
    return () => {
      BoardFilters.removeListener(listener)
    }
  }, [])

  const openStickersSettings = (stickersTab: Tab) => {
    boardSettingsStore.setCurrentTabId('stickers')
    boardSettingsStore.addIntoAdditionalTabInfo({
      currentTab: stickersTab
    })
    boardSettingsStore.toggle()
    void Tracking.send(
      stickersTab === 'board'
        ? TrackingEvent.boardSettingsOpenStickersListViaBoard
        : TrackingEvent.boardSettingsOpenStickersLibraryViaBoard
    )
  }

  const onCreateMenu = useCallback((tippy: TippyInstance) => {
    stickersMenu.current.push(tippy)
  }, [])

  const toggleStickerPanel = () => {
    stickerPanelGlobalStore.togglePanel()

    void Tracking.send(
      stickerPanelGlobalStore.expanded
        ? TrackingEvent.stickerPanelExpandButtonClick
        : TrackingEvent.stickerPanelCollapseButtonClick
    )
  }

  if (!state.isEditAvailable && !store.stickersList.length) {
    return (
      <TwStickerPanelWrapper>
        <div className="ui-cnt" />
      </TwStickerPanelWrapper>
    )
  }

  return (
    <WithResizeObserver className="w-full" type="w" callback={reCountSizes}>
      <TwStickerPanelWrapper ref={panelWrapper}>
        <TwStickerPanel>
          <WithResizeObserver type="w" callback={reCountSizes}>
            <TwLeftArea ref={leftArea} $isEditAvailable={state.isEditAvailable}>
              <WithResizeObserver type="w" callback={reCountSizes}>
                <div className="ui-cnt" />
              </WithResizeObserver>
              {state.isEditAvailable && (
                <IconButton
                  size="small"
                  color="secondary"
                  icon="IconCustomSettings"
                  onClick={() => openStickersSettings('board')}
                />
              )}
            </TwLeftArea>
          </WithResizeObserver>
          <TwStickers ref={stickersList} style={store.stickerPanelStyles}>
            {store.stickersList.map(sticker => {
              const stickerType = StickerExtension.getType(sticker)
              return (
                <WithResizeObserver
                  type="w"
                  key={sticker.id}
                  callback={reCountSizes}
                >
                  <StickerItem
                    withMenu
                    onCreateStickerMenu={onCreateMenu}
                    draggable={!['user', 'timetracking'].includes(stickerType)}
                    sticker={sticker}
                    location="panel"
                    parentLocation={state.location}
                    filtersController={
                      store.emulated ? store.emulated.filter : undefined
                    }
                  />
                </WithResizeObserver>
              )
            })}
            {Boolean(stickerPanelGlobalStore.expanded) &&
              Boolean(state.filtersAmount) && (
                <ButtonNew
                  className="!px-8 !py-4 !text-12 !h-24"
                  onClick={store.resetFilters}
                >
                  {Lang.msg.removeAllBoardFilters(state.filtersAmount)}
                </ButtonNew>
              )}
          </TwStickers>
          <WithResizeObserver type="w" callback={reCountSizes}>
            <TwRightArea ref={rightArea}>
              {state.isMultiline && (
                <TwCollapseButton
                  $short={!stickerPanelGlobalStore.expanded}
                  onClick={toggleStickerPanel}
                >
                  <Icon
                    className="group-hover:text-action-default"
                    icon={
                      stickerPanelGlobalStore.expanded
                        ? 'IconCustomChevronUp'
                        : 'IconCustomChevronDown'
                    }
                  />
                  {!stickerPanelGlobalStore.expanded &&
                    state.hiddenStickersAmount && (
                      <TwHiddenStickersCount
                        $filtersInHiddenArea={!!state.filtersInHiddenArea}
                      >
                        {state.filtersInHiddenArea
                          ? state.filtersInHiddenArea
                          : state.hiddenStickersAmount}
                      </TwHiddenStickersCount>
                    )}
                </TwCollapseButton>
              )}
              {!stickerPanelGlobalStore.expanded &&
                Boolean(state.filtersAmount) && (
                  <ButtonNew
                    className="!px-8 !py-4 !text-12 !h-24 whitespace-nowrap"
                    onClick={store.resetFilters}
                  >
                    {Lang.msg.removeAllBoardFilters(state.filtersAmount)}
                  </ButtonNew>
                )}
              {state.isEditAvailable && (
                <IconButton
                  size="small"
                  color="secondary"
                  icon="IconCustomPlus"
                  onClick={() => openStickersSettings('new')}
                />
              )}
            </TwRightArea>
          </WithResizeObserver>
        </TwStickerPanel>
      </TwStickerPanelWrapper>
    </WithResizeObserver>
  )
})
